import { path } from 'ramda'

/**
 * Tier hierarchies for different subscription types
 * Higher number = higher tier
 */
export const TIER_HIERARCHIES = {
  camera: {
    getTier: (productName) => {
      if (productName.includes('tier2')) return 2 // Gold
      if (productName.includes('tier1')) return 1 // Silver
      return 0 // Standard/Free
    },
    hasAnnual: (productName) => productName.includes('yearly')
  },
  heatStress: {
    getTier: (productName) => {
      if (productName.includes('enterprise')) return 3
      if (productName.includes('pro')) return 2
      if (productName.includes('standard')) return 1
      return 0
    },
    hasAnnual: () => false // All heat stress subscriptions are monthly
  },
  awnPlus: {
    getTier: (productName) => {
      if (productName.includes('for-teams')) return 2
      return 1 // Individual
    },
    hasAnnual: (productName) => productName.includes('yearly')
  }
}

/**
 * Find an existing subscription in the user's subscriptions array
 *
 * @param {Object} user - User object
 * @param {string|null} macAddress - MAC address for device-level subscriptions (camera), null for account-level
 * @param {Function} typeChecker - Function that returns true if productName matches the subscription type
 * @returns {Object|null} - Subscription object or null
 */
export function findExistingSubscription(user, macAddress, typeChecker) {
  const subscriptions = path(['info', 'payments', 'subscriptions'], user) || []

  return subscriptions.find(sub => {
    // Check if subscription type matches
    if (!typeChecker(sub.productName)) return false

    // For camera subscriptions, also match macAddress
    if (macAddress && sub.macAddress !== macAddress) return false

    // For account-level subscriptions (heat stress, AWN+), skip if has macAddress
    // (unless it's explicitly 'Account')
    if (!macAddress && sub.macAddress && sub.macAddress !== 'Account') return false

    return true
  })
}

/**
 * Check if a subscription operation (upgrade/downgrade) was successful
 * Works for all subscription types
 *
 * @param {Object} user - User object
 * @param {string} targetProductName - The product name we're trying to subscribe to
 * @param {string|null} macAddress - MAC address for device-level subscriptions, null for account-level
 * @param {Object|null} initialState - Initial subscription state captured before the operation
 * @returns {boolean} - True if the subscription operation was successful
 */
export function checkSubscriptionSuccess(user, targetProductName, macAddress, initialState) {
  const subscriptions = path(['info', 'payments', 'subscriptions'], user) || []

  // Find the target subscription
  const targetSub = subscriptions.find(sub =>
    sub.productName === targetProductName &&
    (!macAddress || sub.macAddress === macAddress)
  )

  // Check if target subscription is now active (handles upgrades and new subscriptions)
  if (targetSub && (targetSub.status === 'active' || targetSub.status === 'trialing')) {
    return true
  }

  // Check if existing subscription got willDowngradeTo set (handles downgrades)
  if (initialState && initialState.productName) {
    const existingSub = subscriptions.find(sub =>
      sub.productName === initialState.productName &&
      (!macAddress || sub.macAddress === macAddress)
    )

    if (existingSub && initialState.willDowngradeTo !== existingSub.willDowngradeTo) {
      return true
    }
  }

  return false
}

/**
 * Detect if changing from one subscription to another is a downgrade
 *
 * @param {Object} tierHierarchy - Tier hierarchy config from TIER_HIERARCHIES
 * @param {Object} existingSub - Current subscription object
 * @param {string} newProductName - Product name we're changing to
 * @returns {boolean} - True if this is a downgrade
 */
export function detectDowngrade(tierHierarchy, existingSub, newProductName) {
  const currentTier = tierHierarchy.getTier(existingSub.productName)
  const newTier = tierHierarchy.getTier(newProductName)
  const currentAnnual = tierHierarchy.hasAnnual(existingSub.productName)
  const newAnnual = tierHierarchy.hasAnnual(newProductName)

  // Tier comparison
  const isTierUpgrade = currentTier < newTier
  const isTierDowngrade = currentTier > newTier

  // Billing period comparison
  // Only consider billing downgrade if not upgrading tier (tier takes precedence)
  const isBillingDowngrade = currentAnnual && !newAnnual && !isTierUpgrade

  return isTierDowngrade || isBillingDowngrade
}
